import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { EventAvailable as EventIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { apiCall } from '../../utils/fetchAPI';
import { translateApiMessage, translateViolations } from '../../utils/apiMessages';

const EMPTY = { subject: '', preferredAt: '', note: '', location: '' };

/** The fields this form shows, and so the only ones an error can sit under. */
const FIELDS = ['subject', 'preferredAt', 'note', 'location'];

/**
 * Asking a professional for an appointment, from their listing.
 *
 * A form, not a diary. This used to show computed slots from the
 * professional's opening hours, which meant they had to declare their
 * services, their week and their holidays before a resident could ask for
 * anything — half an hour of setup for a promise the hub could not keep while
 * it had no residents. `docs/ARCHIVED_BOOKING_SYSTEM.md` keeps that system on
 * file for the day the demand is there.
 *
 * What is left is what a telephone call would carry: what you want, when would
 * suit you, and anything worth saying. The moment is optional — "quand vous
 * voulez" is a real answer.
 */
function BookingPanel({ provider }) {
  const { isAuthenticated } = useAuth();
  const toast = useToast();

  const [form, setForm] = useState(EMPTY);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (e) => {
    const { name, value } = e.target;

    setForm((previous) => ({ ...previous, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    if (!form.subject.trim()) {
      setFieldErrors({ subject: 'Dites ce que vous souhaitez' });
      return;
    }

    setSending(true);

    try {
      const response = await apiCall('/bookings', {
        method: 'POST',
        body: JSON.stringify({
          providerSlug: provider.slug,
          subject: form.subject.trim(),
          // A datetime-local input has no timezone; the API wants an offset.
          preferredAt: form.preferredAt ? new Date(form.preferredAt).toISOString() : null,
          note: form.note.trim() || null,
          location: form.location.trim() || null,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        const violations = translateViolations(data.violations);
        setFieldErrors(violations);

        if (0 === Object.keys(violations).filter((field) => FIELDS.includes(field)).length) {
          setError(translateApiMessage(data.error) || 'Votre demande n’a pas pu être envoyée.');
        }

        return;
      }

      setSent(true);
      setForm(EMPTY);
      toast.success('Votre demande est partie.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h3" component="h3" sx={{ mb: 1 }}>
          Prendre rendez-vous
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Connectez-vous pour envoyer une demande à {provider.name}.
        </Typography>
        <Button component={RouterLink} to="/login" variant="contained">
          Se connecter
        </Button>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 3 }} component="form" onSubmit={submit} noValidate>
      <Typography variant="h3" component="h3" sx={{ mb: 1 }}>
        Prendre rendez-vous
      </Typography>
      <Typography variant="body2" sx={{ mb: 2.5 }}>
        Dites ce dont vous avez besoin. {provider.name} vous répond pour confirmer ou refuser —
        rien n’est réservé tant que la réponse n’est pas là.
      </Typography>

      {sent && (
        <Alert severity="success" sx={{ mb: 2.5 }}>
          Votre demande est partie. Vous la suivrez depuis «&nbsp;
          <Box component={RouterLink} to="/mes-rendez-vous" sx={{ color: 'inherit' }}>
            Mes rendez-vous
          </Box>
          &nbsp;».
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2.5 }}>
          {error}
        </Alert>
      )}

      <Stack spacing={2.5}>
        <TextField
          label="Ce que vous souhaitez"
          name="subject"
          value={form.subject}
          onChange={update}
          error={Boolean(fieldErrors.subject)}
          helperText={fieldErrors.subject || 'Par exemple : « Fuite sous l’évier de la cuisine »'}
          disabled={sending}
          required
          fullWidth
          inputProps={{ maxLength: 180 }}
        />

        <TextField
          label="Moment souhaité"
          name="preferredAt"
          type="datetime-local"
          value={form.preferredAt}
          onChange={update}
          error={Boolean(fieldErrors.preferredAt)}
          helperText={fieldErrors.preferredAt || 'Facultatif : laissez vide si vous êtes souple'}
          disabled={sending}
          fullWidth
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Précisions"
          name="note"
          value={form.note}
          onChange={update}
          error={Boolean(fieldErrors.note)}
          helperText={fieldErrors.note || 'Ce qui aide à préparer la visite : l’urgence, l’étage, le matériel…'}
          disabled={sending}
          fullWidth
          multiline
          minRows={3}
          inputProps={{ maxLength: 2000 }}
        />

        <TextField
          label="Adresse"
          name="location"
          value={form.location}
          onChange={update}
          error={Boolean(fieldErrors.location)}
          helperText={fieldErrors.location || 'Si l’intervention a lieu chez vous'}
          disabled={sending}
          fullWidth
          inputProps={{ maxLength: 255 }}
        />
      </Stack>

      <Button
        type="submit"
        variant="contained"
        size="large"
        startIcon={<EventIcon />}
        disabled={sending}
        sx={{ mt: 3 }}
      >
        {sending ? 'Envoi…' : 'Envoyer ma demande'}
      </Button>
    </Paper>
  );
}

export default BookingPanel;
